import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Users, Settings, Globe, Menu, X, Code, Share2, HelpingHand, Package, TrendingUp, Star, Building2, Rocket, Zap, Shield, Target, MapPin, Presentation, Store } from 'lucide-react';
import ConnectWithUs from './ConnectWithUs.jsx';

function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const customerTypes = [
    {
      name: 'Foreign DTC Brands Launching in Americas',
      href: '/foreign-dtc-americas',
      icon: Globe,
      description: 'Growth-stage consumer brands expanding from Europe, APAC, or MENA into North American storefronts, marketplaces, and wholesale channels.'
    },
    {
      name: 'Americas (US, Canada, LATAM) DTC Brands Going Global',
      href: '/americas-global',
      icon: Rocket,
      description: 'US and Canadian founders moving into Mexico, LATAM, and India with localized go-to-market, compliance, and fulfillment roadmaps.'
    },
    {
      name: 'Manufacturers (Asia, LATAM) Launching DTC Brands in the Americas',
      href: '/manufacturers-dtc',
      icon: Building2,
      description: 'OEMs and contract manufacturers building direct-to-consumer labels, omnichannel distribution, and owned data infrastructure.'
    }
  ];

  const solutionsDropdown = [
    {
      category: 'Market Entry & Growth Strategy',
      items: [
        { name: 'New Market Entry', href: '/solutions/new-market-entry', icon: Globe },
        { name: 'Product Market Fit', href: '/solutions/product-market-fit', icon: Target }
      ]
    },
    {
      category: 'Channel Development & Sales Acceleration',
      items: [
        { name: 'Online Sales Acceleration', href: '/solutions/online-sales', icon: TrendingUp },
        { name: 'Boutique and Specialty Retail Sales', href: '/solutions/boutique-retail', icon: Star },
        { name: 'Big Box Retail Sales', href: '/solutions/big-box-retail', icon: Building2 },
        { name: 'B2B Sales Acceleration', href: '/solutions/b2b-sales', icon: HelpingHand },
        { name: 'Market Center Entry', href: '/solutions/market-centers', icon: Store },
        { name: 'Trade Show Representation', href: '/solutions/trade-shows', icon: Presentation }
      ]
    },
    {
      category: 'Brand Localization & Marketing',
      items: [
        { name: 'Native Branding', href: '/solutions/native-branding', icon: Zap },
        { name: 'Influencer Marketing', href: '/solutions/influencer-marketing', icon: Users },
        { name: 'Social Media Marketing', href: '/solutions/social-media', icon: Share2 },
        { name: 'Cluster-based Marketing', href: '/solutions/cluster-marketing', icon: MapPin }
      ]
    },
    {
      category: 'Supply Chain & Operational Design',
      items: [
        { name: 'Supply Chain Design and Setup', href: '/solutions/supply-chain', icon: Package },
        { name: 'Tariff Engineering', href: '/solutions/tariff-engineering', icon: Shield }
      ]
    }
  ];

  const marketsDropdown = [
    { name: 'United States', href: '/market-us', flag: '🇺🇸' },
    { name: 'Canada', href: '/market-canada', flag: '🇨🇦' },
    { name: 'Mexico and LATAM', href: '/market-latam', flag: '🇲🇽' },
    { name: 'India', href: '/market-india', flag: '🇮🇳' }
  ];

  const mainNav = [
    { name: 'Technology', href: 'https://tech.motifunbound.com', external: true },
    { name: 'Blog', href: '/blog' }
  ];

  const NavDropdown = ({ title, items, icon: Icon, multiColumn = false, categorized = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    let timeoutId = null;

    const handleMouseEnter = () => {
      if (timeoutId) clearTimeout(timeoutId);
      setIsOpen(true);
      setActiveDropdown(title);
    };

    const handleMouseLeave = () => {
      timeoutId = setTimeout(() => {
        setIsOpen(false);
        setActiveDropdown(null);
      }, 150);
    };

    const getColumns = () => {
      if (categorized) {
        // For categorized items, split categories into two columns
        const midpoint = Math.ceil(items.length / 2);
        return [
          items.slice(0, midpoint),
          items.slice(midpoint)
        ];
      }
      if (!multiColumn) return [items];
      const itemsPerColumn = Math.ceil(items.length / 2);
      return [
        items.slice(0, itemsPerColumn),
        items.slice(itemsPerColumn)
      ];
    };

    const columns = getColumns();

    return (
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button className="flex items-center gap-1 px-4 py-2 text-[#0F3D3F] hover:text-[#BB3E00] transition-colors font-semibold uppercase tracking-[0.18em] text-xs">
          {Icon && <Icon size={18} />}
          {title}
          <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div 
            className={`absolute top-full left-0 mt-1 bg-white shadow-xl rounded-lg border border-gray-200 z-50 ${
              categorized ? 'w-[700px]' : multiColumn ? 'w-[600px]' : 'w-72'
            }`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {categorized ? (
              <div className="grid grid-cols-2 gap-0 p-2">
                {columns.map((columnCategories, colIdx) => (
                  <div key={colIdx} className="border-r border-gray-200 last:border-r-0 px-2">
                    {columnCategories.map((category, catIdx) => (
                      <div key={catIdx} className="mb-4 last:mb-2">
                        <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#38A3A5]">
                          {category.category}
                        </div>
                        <div className="space-y-0.5">
                          {category.items.map((item, idx) => (
                            <Link
                              key={idx}
                              to={item.href}
                              className="block px-3 py-2 hover:bg-gray-50 transition-colors rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                {item.icon && <item.icon size={16} className="text-[#38A3A5] flex-shrink-0" />}
                                <div className="font-medium text-[#000000] text-sm">{item.name}</div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className={`${multiColumn ? 'grid grid-cols-2 gap-0' : ''} p-2`}>
                {columns.map((columnItems, colIdx) => (
                  <div key={colIdx} className={multiColumn ? 'border-r border-gray-200 last:border-r-0' : ''}>
                    {columnItems.map((item, idx) => (
                      <Link
                        key={idx}
                        to={item.href}
                        className="block px-4 py-2.5 hover:bg-gray-50 transition-colors rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {item.icon && <item.icon size={18} className="text-[#38A3A5] flex-shrink-0" />}
                          {item.flag && <span className="text-xl flex-shrink-0">{item.flag}</span>}
                          <div className="font-medium text-[#000000] text-sm">{item.name}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white backdrop-blur">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4 px-6 py-4">
          <Link to="/" className="flex items-center gap-4 hover:opacity-85 transition-opacity">
            <img src="/images/motif-unbound-logo.png" alt="Motif Unbound" className="h-16 w-16 object-contain" />
            <span className="text-2xl font-normal tracking-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
              MOTIF<span className="font-bold text-[#BB3E00]">UNBOUND</span>
            </span>
          </Link>

          <nav className="hidden flex-1 items-center justify-center gap-2 lg:flex">
            <NavDropdown title="Who We Serve" items={customerTypes} icon={Users} />
            <NavDropdown title="Solutions" items={solutionsDropdown} icon={Settings} categorized={true} />
            <NavDropdown title="Markets" items={marketsDropdown} icon={Globe} />
            <a
              href="https://partner.motifunbound.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0F3D3F] transition-colors hover:text-[#BB3E00]"
            >
              Partners
            </a>
            {mainNav.map((item, idx) => (
              item.external ? (
                <a
                  key={idx}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0F3D3F] transition-colors hover:text-[#BB3E00]"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={idx}
                  to={item.href}
                  className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0F3D3F] transition-colors hover:text-[#BB3E00]"
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden xl:block">
              <ConnectWithUs variant="header" />
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#0F3D3F] lg:hidden"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed top-[73px] left-0 right-0 z-40 max-h-[calc(100vh-73px)] overflow-y-auto border-b border-gray-200 bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
            <div className="space-y-2 pt-2">
              <div className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                <Users size={16} className="text-[#38A3A5]" />
                Who We Serve
              </div>
              {customerTypes.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg text-gray-700"
                >
                  <item.icon size={18} className="text-[#38A3A5] flex-shrink-0" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>

            <div className="space-y-2 pt-4 border-t border-gray-200">
              <div className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                <Settings size={16} className="text-[#38A3A5]" />
                Solutions
              </div>
              {solutionsDropdown.map((category, catIdx) => (
                <div key={catIdx} className="mb-4 last:mb-0">
                  <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#38A3A5]">
                    {category.category}
                  </div>
                  {category.items.map((item, idx) => (
                    <Link
                      key={idx}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg text-gray-700"
                    >
                      <item.icon size={18} className="text-[#38A3A5] flex-shrink-0" />
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>

            <div className="space-y-2 pt-4 border-t border-gray-200">
              <div className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                <Globe size={16} className="text-[#38A3A5]" />
                Markets
              </div>
              {marketsDropdown.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg text-gray-700"
                >
                  <span className="text-xl flex-shrink-0">{item.flag}</span>
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>

            <div className="space-y-2 pt-4 border-t border-gray-200">
              <a 
                href="https://partner.motifunbound.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg text-gray-700"
              >
                <HelpingHand size={18} className="text-[#38A3A5] flex-shrink-0" />
                <span>Partners</span>
              </a>
              {mainNav.map((item, idx) => (
                item.external ? (
                  <a 
                    key={idx}
                    href={item.href} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg text-gray-700"
                  >
                    {item.name === 'Technology' ? (
                      <Code size={18} className="text-[#38A3A5] flex-shrink-0" />
                    ) : (
                      <Share2 size={18} className="text-[#38A3A5] flex-shrink-0" />
                    )}
                    <span>{item.name}</span>
                  </a>
                ) : (
                  <Link
                    key={idx}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 rounded-lg text-gray-700"
                  >
                    <Share2 size={18} className="text-[#38A3A5] flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                )
              ))}
            </div>

            <div className="pt-4 border-t border-gray-200">
              <ConnectWithUs variant="header" />
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      <main>
        {children}
      </main>
    </div>
  );
}

export default Layout;
